import React, { Component } from 'react';
import Proptypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';

import styled from 'styled-components';
import { Text, Button } from '@cuemath/comps';

import { closeModal } from 'actions/modal';
import {
  get as getStudentCourses,
  patch as updateStudentCourses,
  invalidateItem as invalidateStudentCourses,
} from 'actions/student-courses';

import { EVENT_TYPE } from 'constants/logger';
import { trackEvent } from 'modules/analytics';
import { assignElectiveFields } from 'constants/form';
import { ELECTIVE_COURSE_ACTIONS, COURSE_TYPE } from 'constants/student-courses';
import { dateObjToEpoch, epochToDate } from 'utils';
import {
  ButtonCenterWrapper,
  FieldWrapper,
  Footer,
  FormWrapper,
  Head,
  Message,
  renderFormField,
} from 'modules/form';

const ReactTableFixedColumns = withFixedColumns(ReactTable);

const FormFooter = styled(Footer)`
  margin: 30px 0;
`;

class ElectiveCoursesModal extends Component {
  static propTypes = {
    actions: Proptypes.shape({
      closeModal: Proptypes.func.isRequired,
      getStudentCourses: Proptypes.func.isRequired,
      updateStudentCourses: Proptypes.func.isRequired,
      invalidateStudentCourses: Proptypes.func.isRequired,
    }).isRequired,
    student: Proptypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      studentCoursesPatch: store.patch(),
      assignedCourses: null,
      unassignedCourses: null,
      allStudentCourses: null,
      formData: {},
      actionType: null,
      confirmText: null,
      infoText: null,
      errorText: null,
      message: null,
      coursesUpdating: false,
      coursesUpdated: false,
    };
  }

  componentDidMount() {
    const { actions, student } = this.props;

    actions.getStudentCourses(student.id, { course_type: COURSE_TYPE.ELECTIVES });
  }

  static getDerivedStateFromProps(props, state) {
    let newState = {};
    const { studentCoursesPatch, formData: oldFormData, coursesUpdating } = state;
    const { student, studentCourses, actions } = props;
    const { id: studentId } = student || {};

    const updateStudentCourses = store.patch(studentCourses, studentCoursesPatch.id);
    const studentCoursesGet = store.get(studentCourses, studentId, {
      course_type: COURSE_TYPE.ELECTIVES,
    });

    if (studentCoursesGet && studentCoursesGet.isLoaded) {
      const studentCourseData = studentCoursesGet.data;

      if (studentCourseData) {
        const {
          assigned_courses: assignedCourses,
          unassigned_courses: unassignedCourses,
          max_core_course_validity: assignedTill,
        } = studentCourseData;

        newState = {
          ...newState,
          assignedCourses,
          unassignedCourses,
          allStudentCourses: assignedCourses.concat(unassignedCourses),
        };

        if (assignedTill && Object.keys(oldFormData).length === 0) {
          newState.formData = { assignedTill: epochToDate(assignedTill, 'YYYY-MM-DD') };
        }
      }
    }
    if (updateStudentCourses.resolved && coursesUpdating) {
      if (updateStudentCourses.resolved.isPatched) {
        if (studentCoursesGet && studentCoursesGet.isLoaded && !studentCoursesGet.isStale)
          actions.invalidateStudentCourses(studentId, {
            course_type: COURSE_TYPE.ELECTIVES,
          });
        newState.coursesUpdated = true;
        newState.message = 'Student electives updated successfully.';
        newState.confirmText = null;
        newState.infoText = null;
        newState.errorText = null;
      } else if (updateStudentCourses.resolved.isPatchingFailed) {
        newState.message = null;
        newState.confirmText = null;
        newState.infoText = null;
        newState.errorText = store.getError(updateStudentCourses.resolved).message;
      }
    }
    return newState;
  }

  onFieldChange = (key, value) => {
    const { formData } = this.state;

    this.setState({ formData: { ...formData, [key]: value } });
  };

  onSelectAction = action => {
    const { ASSIGN_ELECTIVES: ASSIGN_ELECTIVE_ACTION } = ELECTIVE_COURSE_ACTIONS;

    const infoText =
      action === ASSIGN_ELECTIVE_ACTION
        ? '(The default entered date is the max validity of core courses, if assigned.)'
        : null;

    const confirmText =
      action === ASSIGN_ELECTIVE_ACTION
        ? 'Confirm to assign the above unassigned elective courses.'
        : 'Confirm to unassign the above assigned elective courses.';

    this.setState({ actionType: action, confirmText, infoText });
  };

  onSubmit = event => {
    const { actions, student } = this.props;
    const { id: studentId } = student || {};
    const {
      actionType,
      studentCoursesPatch: { id },
    } = this.state;

    const payload = {
      action: actionType,
      action_data: this.getUpdatedFields(),
    };
    trackEvent(EVENT_TYPE.ELECTIVE_COURSES_MODAL_CONFIRM_CLICKED, {
      student_id: studentId,
    });

    this.setState({ coursesUpdating: true }, () => {
      !!student && actions.updateStudentCourses(id, studentId, payload);
    });

    event.preventDefault();
  };

  getUpdatedFields = () => {
    const { formData } = this.state;
    const data = {};

    assignElectiveFields.forEach(field => {
      if (field.type === 'date' && formData[field.name]) {
        data[field.param] = dateObjToEpoch(formData[field.name]);
      } else if (formData.hasOwnProperty(field.name)) {
        data[field.param] = formData[field.name];
      }
    });
    return data;
  };

  renderFormHeader = () => {
    return (
      <Head>
        <Text size="xlarge" align="center">
          Elective Courses
        </Text>
      </Head>
    );
  };

  renderCourseList = () => {
    const {
      allStudentCourses,
      assignedCourses,
      unassignedCourses,
      actionType,
    } = this.state;

    if (!allStudentCourses) return null;

    let courseList = [];
    switch (actionType) {
      case ELECTIVE_COURSE_ACTIONS.ASSIGN_ELECTIVES:
        courseList = unassignedCourses;
      case ELECTIVE_COURSE_ACTIONS.UNASSIGN_ELECTIVES:
        courseList = assignedCourses;
      default:
        courseList = allStudentCourses;
    }

    let columnsData = [
      {
        Header: 'Name',
        accessor: 'course_name',
      },
      {
        Header: 'Stream',
        accessor: 'course_stream',
      },
      {
        Header: 'State',
        accessor: 'state',
        Cell: ({ value }) => (value ? value : 'UNASSIGNED'),
      },
      {
        Header: 'Valid Till',
        accessor: 'validity_till_date',
        Cell: ({ value }) => (value ? epochToDate(value, 'DD-MM-YYYY') : '-'),
      },
      {
        Header: 'Access Type',
        accessor: 'access_type',
        Cell: ({ value }) => (value ? value : '-'),
      },
    ];

    const tableColumns = [
      {
        Header: ``,
        columns: columnsData,
      },
    ];

    return (
      <ReactTableFixedColumns
        data={courseList}
        columns={tableColumns}
        defaultPageSize={courseList.length !== 0 ? courseList.length : 4}
        style={{
          maxHeight: 400,
          minWidth: '150%',
          margin: '40px 0',
          padding: '0 20px',
          textAlign: 'center',
        }}
        className="-striped -highlight"
      />
    );
  };

  renderFormFields = () => {
    const { formData, actionType } = this.state;

    return (
      actionType === ELECTIVE_COURSE_ACTIONS.ASSIGN_ELECTIVES && (
        <FieldWrapper>
          {assignElectiveFields.map(field => {
            return renderFormField({
              ...field,
              value: formData[field.name],
              onChange: value => this.onFieldChange(field.name, value),
            });
          })}
        </FieldWrapper>
      )
    );
  };

  renderFormFooter = () => {
    const { assignedCourses, unassignedCourses, actionType } = this.state;

    if (actionType)
      return (
        <FormFooter>
          <ButtonCenterWrapper>
            <Button align="center" label="Confirm" />
          </ButtonCenterWrapper>
        </FormFooter>
      );

    return null;
  };

  render() {
    const { confirmText, infoText, errorText, message, coursesUpdated } = this.state;

    return (
      <React.Fragment>
        {this.renderFormHeader()}
        {this.renderCourseList()}

        <form onSubmit={this.onSubmit}>
          <fieldset disabled={coursesUpdated}>
            <FormWrapper>
              {this.renderFormFields()}
              {message && <Message>{message}</Message>}
              <br></br>
              <Text size="normal" weight="bold">
                {confirmText}
              </Text>
              <Text size="normal">{infoText}</Text>
              {errorText && <Message error={true}>{errorText}</Message>}
              {this.renderFormFooter()}
            </FormWrapper>
          </fieldset>
        </form>
      </React.Fragment>
    );
  }
}

export default connect(
  ({ studentCourses }) => ({
    studentCourses,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        closeModal,
        getStudentCourses,
        updateStudentCourses,
        invalidateStudentCourses,
      },
      dispatch,
    ),
  }),
)(ElectiveCoursesModal);
